import React, { useRef, useState } from 'react'

interface VideoSource {
  url: string
  mimeType: string
}

export interface SportsBeerTVData {
  youtubeEmbedUrl: string
  videoSources: VideoSource[]
  videoThumbnail: string
  tvImage: string
}

const SportsBeerTV: React.FC<SportsBeerTVData> = ({
  youtubeEmbedUrl,
  videoSources,
  videoThumbnail,
  tvImage,
}) => {
  const [posterVisible, setPosterVisible] = useState(!!videoThumbnail)
  const [showControls, setShowControls] = useState(false)
  const [autoplay, setAutoplay] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const hasVideo = videoSources?.length > 0
  const hasContent = hasVideo || !!youtubeEmbedUrl

  if (!hasContent) return null

  const iframeSrc = autoplay
    ? `${youtubeEmbedUrl}${youtubeEmbedUrl.includes('?') ? '&' : '?'}autoplay=1`
    : youtubeEmbedUrl

  const handlePosterClick = () => {
    setPosterVisible(false)
    if (hasVideo && videoRef.current) {
      setShowControls(true)
      videoRef.current.play()
    } else {
      setAutoplay(true)
    }
  }

  const handleVideoPause = () => {
    setShowControls(false)
    setPosterVisible(true)
  }

  return (
    <div
      className="relative overflow-hidden w-full max-w-[711px]"
      style={{ aspectRatio: '711.8 / 758' }}
    >
      <div className="w-full h-full relative" style={{ padding: '9.5% 10% 35.6%' }}>
        <div className="w-full h-full relative">
          {hasVideo ? (
            <video
              ref={videoRef}
              className="w-full h-full relative"
              poster={videoThumbnail || undefined}
              playsInline
              controls={showControls}
              onPause={handleVideoPause}
            >
              {videoSources.map((source, i) => (
                <source key={i} src={source.url} type={source.mimeType} />
              ))}
            </video>
          ) : (
            <iframe
              src={iframeSrc}
              className="w-full h-full relative"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}

          {videoThumbnail && (
            <div
              className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity duration-500 ${posterVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
              style={{
                backgroundImage: `url('${videoThumbnail}')`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }}
              onClick={handlePosterClick}
            >
              <div className="w-16 h-auto min-[768px]:w-20 transition-transform duration-300 hover:scale-110">
                <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 78 90" fill="none">
                  <path d="M74.7899 41.2253L7.02072 1.55557C4.35412 -0.00536191 1 1.91777 1 5.00763V84.3472C1 87.437 4.35412 89.3602 7.02071 87.7992L74.7899 48.1295C77.429 46.5847 77.429 42.7702 74.7899 41.2253Z" fill="white" stroke="black" strokeWidth="2"/>
                </svg>
              </div>
            </div>
          )}
        </div>

        {tvImage && (
          <img
            src={tvImage}
            alt=""
            className="w-full h-full absolute inset-0 pointer-events-none"
            loading="lazy"
          />
        )}
      </div>
    </div>
  )
}

export default SportsBeerTV
